"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { parseEther } from "viem";
import { WalletListSelector } from "@/app/(v2)/_components/WalletListSelector";
import TokenChainAmountInput from "@/app/(v2)/workflows/_components/form-fields/TokenChainAmountInput";
import { getChainInfo } from "@/lib/chains";
import { filterWalletsByChain } from "@/lib/utils/chain-family";
import { toTurnkeyWallet } from "@/lib/utils/wallet";
import { useWalletsStore } from "@/stores/walletsStore";
import { ERC20_TRANSFER_SIG, ZERO_TX_HASH, padAddressToTopic } from "./evmUtils";
interface ManualTransferFormProps {
  triggerType: string;
  chainId?: number;
  configuredToken?: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
  monitoredAddress?: string;
  onRun: (payload: Record<string, unknown> | null) => void;
}
export function ManualTransferForm(t0) {
  const $ = _c(53);
  const {
    triggerType,
    chainId,
    configuredToken,
    tokenSymbol: initialSymbol,
    tokenDecimals: initialDecimals,
    monitoredAddress,
    onRun
  } = t0;
  const isReceive = triggerType === "erc20-receive" || triggerType === "eth-receive";
  const isNative = triggerType === "eth-send" || triggerType === "eth-receive";
  const t1 = chainId ?? 1;
  let t2;
  if ($[0] !== t1) {
    t2 = getChainInfo(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const nativeSymbol = t2.gasToken;
  const [counterpartyAddress, setCounterpartyAddress] = useState("");
  const [tokenAddress, setTokenAddress] = useState(configuredToken ?? "");
  const [selectedChainId, setSelectedChainId] = useState(chainId ?? 1);
  const [tokenSymbol, setTokenSymbol] = useState(initialSymbol ?? "");
  const [tokenDecimals, setTokenDecimals] = useState(initialDecimals ?? 18);
  const [amountWei, setAmountWei] = useState("");
  const [nativeAmount, setNativeAmount] = useState("");
  const {
    wallets: orgWallets,
    loading: walletsLoading,
    loadWallets
  } = useWalletsStore();
  let t3;
  let t4;
  if ($[2] !== loadWallets) {
    t3 = () => {
      loadWallets();
    };
    t4 = [loadWallets];
    $[2] = loadWallets;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== chainId || $[6] !== orgWallets) {
    t6 = filterWalletsByChain(orgWallets.map(toTurnkeyWallet), chainId);
    $[5] = chainId;
    $[6] = orgWallets;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  t5 = t6;
  const walletOptions = t5;
  const onRunRef = useRef(onRun);
  let t7;
  if ($[8] !== onRun) {
    t7 = () => {
      onRunRef.current = onRun;
    };
    $[8] = onRun;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t7);
  let t8;
  if ($[10] !== amountWei || $[11] !== counterpartyAddress || $[12] !== isNative || $[13] !== isReceive || $[14] !== monitoredAddress || $[15] !== nativeAmount || $[16] !== nativeSymbol || $[17] !== selectedChainId || $[18] !== tokenAddress || $[19] !== tokenDecimals || $[20] !== tokenSymbol) {
    t8 = () => {
      const hasCounterparty = counterpartyAddress.length > 0;
      let effectiveAmountWei;
      if (isNative) {
        const trimmed = nativeAmount.trim();
        if (!trimmed) {
          effectiveAmountWei = "";
        } else {
          try {
            effectiveAmountWei = parseEther(trimmed).toString();
          } catch {
            effectiveAmountWei = "";
          }
        }
      } else {
        effectiveAmountWei = amountWei;
      }
      const hasAmount = effectiveAmountWei.length > 0 && effectiveAmountWei !== "0";
      const hasToken = isNative || tokenAddress.length > 0;
      if (!hasCounterparty || !hasAmount || !hasToken || !monitoredAddress) {
        onRunRef.current(null);
        return;
      }
      const sender = isReceive ? counterpartyAddress : monitoredAddress ?? "";
      const recipient = isReceive ? monitoredAddress ?? "" : counterpartyAddress;
      if (isNative) {
        onRunRef.current({
          triggeredAt: new Date().toISOString(),
          chainId: selectedChainId,
          txHash: ZERO_TX_HASH,
          from: sender,
          to: recipient,
          value: effectiveAmountWei,
          blockNumber: 0,
          _symbol: tokenSymbol || nativeSymbol,
          _decimals: 18
        });
      } else {
        const result = {
          params: {
            from: sender,
            to: recipient,
            value: effectiveAmountWei
          },
          raw: {
            address: tokenAddress,
            blockNumber: 0,
            transactionHash: ZERO_TX_HASH,
            logIndex: 0,
            topics: [ERC20_TRANSFER_SIG, padAddressToTopic(sender), padAddressToTopic(recipient)]
          },
          triggeredAt: new Date().toISOString()
        };
        if (tokenSymbol) {
          result._symbol = tokenSymbol;
        }
        result._decimals = tokenDecimals;
        onRunRef.current(result);
      }
    };
    $[10] = amountWei;
    $[11] = counterpartyAddress;
    $[12] = isNative;
    $[13] = isReceive;
    $[14] = monitoredAddress;
    $[15] = nativeAmount;
    $[16] = nativeSymbol;
    $[17] = selectedChainId;
    $[18] = tokenAddress;
    $[19] = tokenDecimals;
    $[20] = tokenSymbol;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== amountWei || $[23] !== counterpartyAddress || $[24] !== isNative || $[25] !== isReceive || $[26] !== monitoredAddress || $[27] !== nativeAmount || $[28] !== selectedChainId || $[29] !== tokenAddress || $[30] !== tokenDecimals || $[31] !== tokenSymbol) {
    t9 = [counterpartyAddress, tokenAddress, amountWei, nativeAmount, isReceive, isNative, selectedChainId, monitoredAddress, tokenSymbol, tokenDecimals];
    $[22] = amountWei;
    $[23] = counterpartyAddress;
    $[24] = isNative;
    $[25] = isReceive;
    $[26] = monitoredAddress;
    $[27] = nativeAmount;
    $[28] = selectedChainId;
    $[29] = tokenAddress;
    $[30] = tokenDecimals;
    $[31] = tokenSymbol;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  useEffect(t8, t9);
  let t10;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = (address, newChainId, symbol, decimals) => {
      setTokenAddress(address);
      setSelectedChainId(newChainId);
      setTokenSymbol(symbol);
      setTokenDecimals(decimals);
    };
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const handleTokenChange = t10;
  const t11 = isReceive ? "From" : "To";
  let t12;
  if ($[34] !== t11) {
    t12 = <label className="field-label mb-1.5 block">{t11}</label>;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== counterpartyAddress || $[37] !== walletOptions || $[38] !== walletsLoading) {
    t13 = <WalletListSelector wallets={walletOptions} selectedAddress={counterpartyAddress} onSelect={setCounterpartyAddress} isLoading={walletsLoading} variant="compact" />;
    $[36] = counterpartyAddress;
    $[37] = walletOptions;
    $[38] = walletsLoading;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t12 || $[41] !== t13) {
    t14 = <div>{t12}{t13}</div>;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== amountWei || $[44] !== isNative || $[45] !== nativeAmount || $[46] !== nativeSymbol || $[47] !== selectedChainId || $[48] !== tokenAddress) {
    t15 = isNative ? <div><label className="field-label mb-1.5 block">Amount ({nativeSymbol})</label><input type="text" inputMode="decimal" value={nativeAmount} onChange={e => {
        if (e.target.value && !/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
          return;
        }
        setNativeAmount(e.target.value);
      }} placeholder="0.00" className="border-ds-border-default text-ds-text-primary placeholder:text-ds-text-disabled bg-ds-surface-card w-full rounded-xl border px-4 py-3 text-lg font-semibold outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 dark:hover:border-zinc-600" /></div> : <TokenChainAmountInput label="Token & amount" amount={amountWei} onAmountChange={setAmountWei} tokenAddress={tokenAddress} chainId={selectedChainId} onTokenChange={handleTokenChange} popoverModal={true} excludeNative={true} />;
    $[43] = amountWei;
    $[44] = isNative;
    $[45] = nativeAmount;
    $[46] = nativeSymbol;
    $[47] = selectedChainId;
    $[48] = tokenAddress;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== t14 || $[51] !== t15) {
    t16 = <div className="space-y-4">{t14}{t15}</div>;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  return t16;
}